/**
 * Phase 57 Plan 57-03 — PORTAL-03
 *
 * Conversation-centric request detail panel.
 *
 * Layout: breadcrumb → title → collapsible description → chip row →
 * conversation thread (TRUE chronological interleave of comments + state-change
 * system lines) → sticky reply composer.
 *
 * Auth isolation: NEVER imports from services/authContext (Landmine #8).
 * Uses useExternalCurrentUser(true) for the actor identity.
 */
import { useState } from 'react';
import { format } from 'date-fns';
import { cs } from 'date-fns/locale';
import { useComments } from '../../hooks/useComments';
import { useTaskActivity } from '../../hooks/useTaskActivity';
import { useExternalCurrentUser } from '../../hooks/useExternalCurrentUser';
import { usePortalRequests } from '../../hooks/usePortalRequests';
import { useTaskAttachments } from '../../hooks/useTaskAttachments';
import { CommentBubble } from '../comments/CommentBubble';
import type { BubbleActor } from '../comments/CommentBubble';
import { AuthorReviewActions } from '../kanban/AuthorReviewActions';
import { ReplyComposer } from './ReplyComposer';
import { STATUS_PILL } from './RequestsRail';
import { getTierColor, chipTextColor } from '../../utils/format';
import type { TaskActivityEvent } from '../../hooks/useTaskActivity';

const TIER_LABEL: Record<number, string> = {
  1: 'URGENT',
  2: 'HIGH',
  3: 'NORMAL',
  4: 'LOW',
};

/** "First L." format — e.g. "Lucie Bartosikova" -> "Lucie B." */
function formatShortName(fullName: string): string {
  const parts = fullName.trim().split(/\s+/);
  if (parts.length < 2) return parts[0] ?? '';
  return `${parts[0]} ${parts[parts.length - 1]![0]}.`;
}

/**
 * Derive a short display label from an activity event.
 *
 * Quick task 260809-qcy — resolves the actor with the SAME precedence as
 * the internal timeline (TaskActivityTimeline.tsx resolvedActorName):
 * internal `user` first, then the external actor resolved from
 * `changes.actor`, then the "System" fallback. A future edit to one should
 * prompt an edit to the other.
 */
function activitySystemLine(event: TaskActivityEvent): string {
  const { subjectType, state, changes, user, externalActor } = event;
  const actorFull = user
    ? [user.firstName, user.lastName].filter(Boolean).join(' ') || user.email
    : externalActor
      ? [externalActor.firstName, externalActor.lastName].filter(Boolean).join(' ') ||
        externalActor.email
      : 'System';
  const actorShort = actorFull ? formatShortName(actorFull) : 'System';

  function payloadValue(key: string): unknown {
    if (!changes || Array.isArray(changes)) return undefined;
    return (changes as Record<string, unknown>)[key];
  }

  if (subjectType === 'task' && state === 'state_changed') {
    const before = String(payloadValue('before') ?? '?');
    const after = String(payloadValue('after') ?? '?');
    return `Status: ${before} → ${after} by ${actorShort}`;
  }
  if (subjectType === 'task_assignee' && state === 'assignee_changed') {
    return `Assignee changed by ${actorShort}`;
  }
  if (subjectType === 'task_assignee' && state === 'author_approved') {
    return `Ticket approved by ${actorShort}`;
  }
  if (subjectType === 'task_assignee' && state === 'author_reopened') {
    const reason = payloadValue('reason');
    return typeof reason === 'string' && reason.length > 0
      ? `Ticket reopened by ${actorShort}: “${reason}”`
      : `Ticket reopened by ${actorShort}`;
  }
  if (subjectType === 'task_priority' && state === 'priority_changed') {
    const after = payloadValue('after');
    return `Priority set to ${String(after ?? '—')} by ${actorShort}`;
  }
  if (subjectType === 'task_lifecycle') {
    if (state === 'task_lifecycle.created') return `Request created by ${actorShort}`;
    if (state === 'milestone_changed') return `Milestone changed by ${actorShort}`;
  }
  // Generic fallback
  return `${subjectType.replace(/_/g, ' ')} · ${state.replace(/_/g, ' ')} by ${actorShort}`;
}

/** Format a timestamp for system lines: "Po 10:22" */
function formatSystemTime(iso: string | null): string {
  if (!iso) return '';
  try {
    return format(new Date(iso), 'EEE HH:mm', { locale: cs });
  } catch {
    return iso;
  }
}

// ─── Merged thread item types ───────────────────────────────────────────────

type ThreadItem =
  | { kind: 'comment'; at: number; comment: import('../../services/api/comments').CommentDTO }
  | { kind: 'system'; at: number; event: TaskActivityEvent };

// ─── Shimmer skeleton ────────────────────────────────────────────────────────

function ShimmerBubble({ right }: { right?: boolean }) {
  return (
    <div className={`flex ${right ? 'justify-end' : 'justify-start'} mb-3`}>
      <div className="h-16 w-2/3 bg-ms-border rounded-lg animate-pulse" />
    </div>
  );
}

// ─── Props ────────────────────────────────────────────────────────────────────

export interface RequestDetailProps {
  taskId: number;
  projectId: number | string;
}

// ─── Component ────────────────────────────────────────────────────────────────

export function RequestDetail({ taskId, projectId }: RequestDetailProps) {
  const [showFullDescription, setShowFullDescription] = useState(false);

  // External actor identity — portal always has an external session
  const extUserQuery = useExternalCurrentUser(true);
  const actor: BubbleActor | null = extUserQuery.data
    ? { kind: 'external_user', id: extUserQuery.data.id }
    : null;

  // Fetch task from portal board cache
  const boardQuery = usePortalRequests(String(projectId));
  const task = boardQuery.data
    ? boardQuery.data.columns
        .flatMap((col) => col.tasks)
        .find((t) => t.id === taskId) ?? null
    : null;

  // Conversation data
  const commentsQuery = useComments(taskId);
  const activityQuery = useTaskActivity(taskId);

  // 260809-ofp (D-03) — attachments the customer (or the triaging team)
  // uploaded on this ticket. DELETE is internal-only under D-01 and would
  // always 403 for a portal user, so no delete control is rendered here.
  const attachmentsQuery = useTaskAttachments(taskId);

  // ── Derived chip-row data ────────────────────────────────────────────────

  // Determine status key from board column. Quick task 260810-l18 — capture
  // the column's isTerminal alongside statusKey (same loop, no re-scan) so
  // the author-review gate below doesn't need a second pass over columns.
  let statusKey = 'triage';
  let columnIsTerminal = false;
  if (boardQuery.data) {
    for (const col of boardQuery.data.columns) {
      if (col.tasks.some((t) => t.id === taskId)) {
        columnIsTerminal = col.isTerminal ?? false;
        if (col.isTriage) {
          statusKey = 'triage';
        } else {
          const lower = col.name.toLowerCase().replace(/\s+/g, '_');
          statusKey = lower === 'done' ? 'done' : lower === 'submitted' ? 'submitted' : 'in_progress';
        }
        break;
      }
    }
  }
  const pillConfig = STATUS_PILL[statusKey];
  // Quick task 260810-l18 — the AUTHOR of a finished request sees Reopen.
  // Client-side gating is an affordance only; the service re-checks both the
  // author gate and the terminal gate, so any 4xx just becomes a toast.
  const isAuthor =
    extUserQuery.data != null && task != null && extUserQuery.data.id === task.createdByExternalId;
  const showAuthorReview = isAuthor && columnIsTerminal;

  const tierBg = task?.tier ? getTierColor(task.tier.orderIndex) : null;
  const tierLabel = task?.tier ? (TIER_LABEL[task.tier.orderIndex] ?? null) : null;
  const tierTextColor = tierBg ? chipTextColor(tierBg) : '#ffffff';

  const assigneeShort = task?.assigneeName ? formatShortName(task.assigneeName) : null;

  // ── Conversation: TRUE chronological interleave ──────────────────────────

  const commentItems: ThreadItem[] = (commentsQuery.data ?? []).map((c) => ({
    kind: 'comment',
    at: new Date(c.createdAt).getTime(),
    comment: c,
  }));

  const activityItems: ThreadItem[] = (activityQuery.data ?? []).map((e) => ({
    kind: 'system',
    at: e.createdAt ? new Date(e.createdAt).getTime() : 0,
    event: e,
  }));

  const mergedThread: ThreadItem[] = [...commentItems, ...activityItems].sort(
    (a, b) => a.at - b.at,
  );

  // ── Loading skeleton (board not loaded yet) ──────────────────────────────

  if (boardQuery.isLoading && !task) {
    return (
      <div className="flex-1 flex flex-col p-6">
        <div className="h-4 w-48 bg-ms-border rounded animate-pulse mb-4" />
        <div className="h-8 w-3/4 bg-ms-border rounded animate-pulse mb-2" />
        <div className="h-4 w-full bg-ms-border rounded animate-pulse mb-1" />
        <div className="h-4 w-5/6 bg-ms-border rounded animate-pulse" />
      </div>
    );
  }

  const title = task?.name ?? `Request #${taskId}`;
  const description = task?.description ?? '';
  const code = task?.code ?? '';
  const createdAt = task?.createdAt
    ? format(new Date(task.createdAt), 'EEE d. M., HH:mm', { locale: cs })
    : '';

  const threadLoading = commentsQuery.isLoading || activityQuery.isLoading;
  const threadError = commentsQuery.isError || activityQuery.isError;

  return (
    <div className="flex-1 overflow-y-auto flex flex-col">
      {/* ── Header ──────────────────────────────────────────────────────── */}
      <div className="px-6 pt-6 pb-4 border-b border-ms-border">
        {/* Breadcrumb */}
        <p className="text-xs text-ms-text-secondary mb-2">
          Requests{code ? ` / ${code}` : ''}
          {createdAt ? ` · Opened ${createdAt}` : ''}
        </p>

        {/* Title */}
        <h2 className="text-2xl font-bold text-ms-text-strong mb-3 break-words">{title}</h2>

        {/* Description block */}
        {description ? (
          <div className="mb-4">
            <p
              className={`text-sm text-ms-text leading-relaxed ${
                showFullDescription ? '' : 'line-clamp-3'
              }`}
            >
              {description}
            </p>
            <button
              type="button"
              onClick={() => setShowFullDescription((v) => !v)}
              className="text-xs text-ms-text-secondary hover:text-ms-text mt-1"
            >
              {showFullDescription ? 'Show less ↑' : 'Show more ↓'}
            </button>
          </div>
        ) : null}

        {/* Attachments (260809-ofp D-03) */}
        <div className="mb-4">
          <p className="text-xs font-semibold uppercase tracking-wider text-ms-text-muted mb-1.5">
            Attachments
          </p>
          {attachmentsQuery.isLoading ? (
            <div className="h-4 w-32 bg-ms-border rounded animate-pulse" />
          ) : attachmentsQuery.data.length === 0 ? (
            <p className="text-sm text-ms-text-muted">No attachments yet.</p>
          ) : (
            <ul className="space-y-1">
              {attachmentsQuery.data.map((att) => (
                <li key={att.id} className="text-sm">
                  <a
                    href={att.downloadUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-ms-orange hover:underline truncate inline-block max-w-full align-bottom"
                  >
                    {att.filename}
                  </a>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Chip row */}
        <div className="flex flex-wrap items-center gap-2">
          {/* Status pill */}
          {pillConfig ? (
            <span
              className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full ${pillConfig.bg} ${pillConfig.text}`}
            >
              <span
                className="w-1.5 h-1.5 rounded-full flex-shrink-0"
                style={{ backgroundColor: pillConfig.dot }}
              />
              {pillConfig.label}
            </span>
          ) : null}

          {/* Priority badge */}
          {tierBg && tierLabel ? (
            <span
              className="text-[10px] font-semibold font-mono uppercase tracking-wide px-1.5 py-0.5 rounded"
              style={{ backgroundColor: tierBg, color: tierTextColor }}
            >
              {tierLabel}
            </span>
          ) : null}

          {/* Project tag */}
          <span className="bg-ms-surface-2 border border-ms-border rounded-full px-2 py-1 text-xs font-medium text-ms-text">
            {String(projectId)}
          </span>

          {/* Assignee chip */}
          {assigneeShort ? (
            <span className="inline-flex items-center gap-1.5">
              <span className="text-[10px] font-semibold uppercase tracking-wider text-ms-text-muted">
                ASSIGNEE
              </span>
              <span className="bg-ms-surface-2 border border-ms-border rounded-full px-2 py-1 text-xs font-medium text-ms-text-strong">
                {assigneeShort}
              </span>
            </span>
          ) : null}

          {/* Subscribe button (disabled v1) */}
          <button
            type="button"
            disabled
            className="text-xs font-medium px-2 py-1 rounded-full border border-ms-border text-ms-text-secondary opacity-50 pointer-events-none"
          >
            Subscribe
          </button>
        </div>

        {/* Quick task 260810-l18 — author-only Approve/Reopen panel. */}
        {showAuthorReview ? (
          <AuthorReviewActions taskId={taskId} projectId={Number(projectId)} audience="external" />
        ) : null}
      </div>

      {/* ── Conversation thread (TRUE chronological interleave) ─────────── */}
      <div className="flex-1 px-6 py-4 space-y-3 overflow-y-auto">
        {threadLoading ? (
          <>
            <ShimmerBubble />
            <ShimmerBubble right />
          </>
        ) : threadError ? (
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <p className="text-sm text-ms-text-muted mb-2">Could not load conversation.</p>
            <button
              type="button"
              onClick={() => {
                void commentsQuery.refetch();
                void activityQuery.refetch();
              }}
              className="text-xs text-ms-text-secondary hover:text-ms-text underline"
            >
              Retry
            </button>
          </div>
        ) : mergedThread.length === 0 ? (
          <p className="text-sm text-ms-text-muted text-center py-8">
            No messages yet. Start the conversation below.
          </p>
        ) : (
          mergedThread.map((item, idx) => {
            if (item.kind === 'comment') {
              return actor ? (
                <CommentBubble
                  key={`c${item.comment.id}`}
                  taskId={taskId}
                  comment={item.comment}
                  actor={actor}
                />
              ) : null;
            }
            // System line — centered muted text (not a bubble)
            const timeStr = formatSystemTime(item.event.createdAt);
            const label = activitySystemLine(item.event);
            return (
              <div
                key={`s${idx}`}
                className="text-xs text-ms-text-muted italic bg-ms-surface-2 rounded px-3 py-1 mx-auto text-center"
              >
                {timeStr ? `${timeStr} · ` : ''}{label}
              </div>
            );
          })
        )}
      </div>

      {/* ── Sticky reply composer ────────────────────────────────────────── */}
      <ReplyComposer
        taskId={taskId}
        projectId={Number(projectId)}
        requestTitle={title}
        onPosted={() => void commentsQuery.refetch()}
      />
    </div>
  );
}
